import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Box,
  CircularProgress,
  IconButton,
  Typography,
  Divider,
  InputAdornment,
  Autocomplete
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getCategories, getBins, getVendors } from '../../services/productapi';

const initialFormData = {
  name: '',
  category: '',
  location: '',
  vendor: '',
  material: '',
  mrp: '0.00',
  cost_price: '0.00',
  state: 'active',
  blouse: '',
  description: '',
  bp1: '',
  bp2: '',
  bp3: '',
  bp4: ''
};

const ProductFormModal = ({ open, onClose, onSave, product = null, isLoading = false }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [bins, setBins] = useState([]);
  const [binsLoading, setBinsLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  // Fetch reference data for dropdowns
  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        setCategoriesLoading(true);
        setBinsLoading(true);
        setVendorsLoading(true);
        
        const [categoriesResponse, binsResponse, vendorsResponse] = await Promise.all([
          getCategories(),
          getBins(),
          getVendors()
        ]);
        
        // Categories are not paginated anymore, but handle both formats just to be safe
        if (categoriesResponse.data.results) {
          setCategories(categoriesResponse.data.results);
        } else {
          setCategories(categoriesResponse.data);
        }
        
        // Handle bins data
        if (binsResponse.data.results) {
          setBins(binsResponse.data.results);
        } else {
          setBins(binsResponse.data);
        }
        
        // Handle vendors data
        if (vendorsResponse.data.results) {
          setVendors(vendorsResponse.data.results);
        } else {
          setVendors(vendorsResponse.data);
        }
        
        setCategoriesLoading(false);
        setBinsLoading(false);
        setVendorsLoading(false);
      } catch (error) {
        console.error('Error fetching reference data:', error);
        setCategoriesLoading(false);
        setBinsLoading(false);
        setVendorsLoading(false);
      }
    };
    
    if (open) {
      fetchReferenceData();
    }
  }, [open]);
  // If editing, populate form with product data
  useEffect(() => {
    if (product) {
      // Log the data for debugging
      console.log('Editing product with data:', product);
      
      setFormData({
        name: product.name || '',
        category: product.category || '',
        location: product.location || '',
        vendor: product.vendor || '',
        material: product.material || '',
        mrp: product.mrp !== undefined ? product.mrp.toString() : '0.00',
        cost_price: product.cost_price !== undefined ? product.cost_price.toString() : '0.00',
        state: product.state || 'active',
        blouse: product.blouse || '',
        description: product.description || '',
        bp1: product.bp1 || '',
        bp2: product.bp2 || '',
        bp3: product.bp3 || '',
        bp4: product.bp4 || ''
      });
    } else {
      setFormData(initialFormData);
    }
    
    // Clear errors when modal opens/closes or product changes
    setErrors({});
  }, [product, open]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error for this field if it was previously set
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.name) newErrors.name = 'Product name is required';
    if (!formData.category) newErrors.category = 'Category is required';
      // Validate numeric fields (only if they're provided)
    if (formData.mrp && isNaN(Number(formData.mrp))) {
      newErrors.mrp = 'MRP must be a number';
    }
    
    if (formData.cost_price && isNaN(Number(formData.cost_price))) {
      newErrors.cost_price = 'Cost price must be a number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };  const handleSubmit = () => {
    if (validateForm()) {
      // Start with a copy of the form data
      const dataToSave = { ...formData };
      
      // Convert numeric strings to actual numbers with default to 0.00
      dataToSave.mrp = formData.mrp ? parseFloat(formData.mrp) : 0.00;
      dataToSave.cost_price = formData.cost_price ? parseFloat(formData.cost_price) : 0.00;
      
      // Log final data being sent
      console.log('Submitting product data:', dataToSave);
      console.log('Selected vendor ID:', dataToSave.vendor);
      
      onSave(dataToSave, product ? 'edit' : 'add');
    }
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: '#f8f9fa',
        p: 2
      }}>
        <Typography variant="h6">
          {product ? 'Edit Product' : 'Add New Product'}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <Divider />
      
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Product Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.name}
            helperText={errors.name}
            size="small"
          />
            <Autocomplete
            fullWidth
            size="small"
            options={categories}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return option?.name || '';
            }}
            isOptionEqualToValue={(option, value) => {
              if (!option || !value) return false;
              return option.id === value.id;
            }}
            value={categories.find(category => category.id === formData.category) || null}
            onChange={(event, newValue) => {
              setFormData({
                ...formData,
                category: newValue ? newValue.id : ''
              });
              
              // Clear category error if it was set
              if (errors.category) {
                setErrors({
                  ...errors,
                  category: null
                });
              }
            }}
            loading={categoriesLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Category "
                required
                error={!!errors.category}
                helperText={errors.category}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {categoriesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                {option.name}
              </Box>
            )}
            noOptionsText="No categories found"
          />
          
          <Autocomplete
            fullWidth
            size="small"
            options={bins}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return option?.name || '';
            }}
            isOptionEqualToValue={(option, value) => {
              if (!option || !value) return false;
              return option.id === value.id;
            }}
            value={bins.find(bin => bin.id === formData.location) || null}
            onChange={(event, newValue) => {
              setFormData({
                ...formData,
                location: newValue ? newValue.id : ''
              });
            }}
            loading={binsLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Location (Bin)"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {binsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                {option.name}
              </Box>
            )}
            noOptionsText="No bins found"
          />
         
          <Autocomplete
            fullWidth
            size="small"
            options={vendors}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return option?.name || '';
            }}
            isOptionEqualToValue={(option, value) => {
              if (!option || !value) return false;
              return option.id === value.id;
            }}
            value={vendors.find(vendor => vendor.id === formData.vendor) || null}
            onChange={(event, newValue) => {
              setFormData({
                ...formData,
                vendor: newValue ? newValue.id : ''
              });
            }}
            loading={vendorsLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Vendor"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {vendorsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                {option.name}
              </Box>
            )}
            noOptionsText="No vendors found"
          />
          
          <TextField
            label="Material"
            name="material"
            value={formData.material }
            onChange={handleChange}
            fullWidth
            size="small"
          />
            <TextField
            label="MRP"
            name="mrp"
            type="number"
            value={formData.mrp}
            onChange={handleChange}
            fullWidth
            error={!!errors.mrp}
            helperText={errors.mrp}
            size="small"
            InputProps={{
              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
            }}
          />
            <TextField
            label="Cost Price"
            name="cost_price"
            type="number"
            value={formData.cost_price}
            onChange={handleChange}
            fullWidth
            error={!!errors.cost_price}
            helperText={errors.cost_price}
            size="small"
            InputProps={{
              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
            }}
          />
          
          <FormControl fullWidth size="small">
            <InputLabel id="status-label">Status</InputLabel>
            <Select
              labelId="status-label"
              name="state"
              value={formData.state}
              onChange={handleChange}
              label="Status"
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="discontinued">Discontinued</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Blouse"
            name="blouse"
            value={formData.blouse}
            onChange={handleChange}
            fullWidth
            size="small"
          />
          <TextField
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            fullWidth
            size="small"
            multiline
            rows={3}
          />
          <TextField
            label="BP1"
            name="bp1"
            value={formData.bp1}
            onChange={handleChange}
            fullWidth
            size="small"
          />
          <TextField
            label="BP2"
            name="bp2"
            value={formData.bp2}
            onChange={handleChange}
            fullWidth
            size="small"
          /><TextField
            label="BP3"
            name="bp3"
            value={formData.bp3}
            onChange={handleChange}
            fullWidth
            size="small"
          /><TextField
            label="BP4"
            name="bp4"
            value={formData.bp4}
            onChange={handleChange}
            fullWidth
            size="small"
          />
        </Box>
      </DialogContent>
      
      <Divider />
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={isLoading}
          sx={{ 
            bgcolor: '#3f51b5', 
            '&:hover': { bgcolor: '#303f9f' },
            textTransform: 'none'
          }}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? 'Saving...' : product ? 'Update Product' : 'Add Product'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductFormModal;
